import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import * as React from 'react'
import { cn } from '@/lib/utils'

interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: string | React.ReactNode
  className?: string
  indicatorClassName?: string
  indeterminate?: boolean
  labelClassName?: string
  containerClassName?: string
  variant?: 'primary' | 'secondary'
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      label,
      className,
      indicatorClassName,
      labelClassName,
      containerClassName,
      indeterminate = false,
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    const checked = indeterminate ? 'indeterminate' : props.checked

    // Mousedown event listener to prevent text selection
    const handleMouseDown = (e: React.MouseEvent<HTMLLabelElement>) => {
      if (e.shiftKey) {
        e.preventDefault()
      }
    }

    return (
      <label
        className={cn(
          `relative flex w-fit items-center gap-1 ${props.disabled ? 'cursor-default' : 'cursor-pointer'}`,
          containerClassName,
        )}
        htmlFor={props.id}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onMouseDown={handleMouseDown}
      >
        <CheckboxPrimitive.Root
          tabIndex={0}
          ref={ref}
          className={cn(
            'shrink-0 flex h-4 w-4 items-center justify-center rounded-[2px] border border-gray-5 bg-white text-white',
            (checked === true || checked === 'indeterminate') &&
              (variant === 'primary' ? 'bg-blue-9 border-blue-9' : 'bg-gray-12 border-gray-12'),
            props.disabled && 'pointer-events-none cursor-default',
            props.disabled &&
              (checked === true || checked === 'indeterminate') &&
              'bg-gray-8 border-none',
            className,
          )}
          {...props}
          checked={checked}
          id={props.id || 'checkbox'}
        >
          <CheckboxPrimitive.Indicator
            className={cn(indicatorClassName, props.disabled && 'text-white')}
          >
            {/* Show Minus if indeterminate */}
            {checked === 'indeterminate' ? (
              <Minus className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {label && (
          <div className={cn('ps-2 text-xm', props.disabled && 'text-gray-8', labelClassName)}>
            {label}
          </div>
        )}
      </label>
    )
  },
)

Checkbox.displayName = 'Checkbox'
